import { useState } from "react";
import { X, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import "./ConsultationModal.css";

const ConsultationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    website: "",
    phone: "",
    email: "",
    message: "",
    honeypot: "", // Spam protection
    mathAnswer: "",
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // Generate random math question for spam protection
  const [mathQuestion] = useState(() => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    return { num1, num2, answer: num1 + num2 };
  });

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    
    if (!formData.website.trim()) {
      newErrors.website = "Website is required";
    } else if (!/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.website)) {
      newErrors.website = "Please enter a valid website URL";
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\d\s\-\+\(\)]{10,}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }
    
    if (parseInt(formData.mathAnswer) !== mathQuestion.answer) {
      newErrors.mathAnswer = "Incorrect answer";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot (spam protection)
    if (formData.honeypot) {
      return; // Bot detected
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/consultation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          message: formData.message,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSubmitted(true);
        
        // Reset form after delay
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            firstName: "",
            lastName: "",
            website: "",
            phone: "",
            email: "",
            message: "",
            honeypot: "",
            mathAnswer: "",
          });
          onClose();
        }, 3000);
      } else {
        setErrors({ submit: data.message || 'Failed to submit. Please try again.' });
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrors({ submit: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="consultation-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          <X size={24} />
        </button>
        
        {isSubmitted ? (
          <div className="modal-success">
            <div className="success-icon">
              <CheckCircle2 size={48} />
            </div>
            <h2>Thank You!</h2>
            <p>We've received your consultation request. Our team will be in touch within 24 hours.</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2>Schedule a Consultation</h2>
              <p>Tell us about your customer acquisition goals and we'll show you how IntentLogic can help.</p>
            </div>
            
            <form onSubmit={handleSubmit} className="consultation-form">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
              />
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName ? "error" : ""}
                    placeholder="John"
                    data-testid="input-first-name"
                  />
                  {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName ? "error" : ""}
                    placeholder="Smith"
                    data-testid="input-last-name"
                  />
                  {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="website">Website *</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className={errors.website ? "error" : ""}
                  placeholder="https://yourcompany.com"
                  data-testid="input-website"
                />
                {errors.website && <span className="error-text">{errors.website}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? "error" : ""}
                    placeholder="+1 (555) 123-4567"
                    data-testid="input-phone"
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? "error" : ""}
                    placeholder="john@company.com"
                    data-testid="input-email"
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={errors.message ? "error" : ""}
                  placeholder="Tell us about your customer acquisition goals..."
                  rows={4}
                  data-testid="input-message"
                />
                {errors.message && <span className="error-text">{errors.message}</span>}
              </div>
              
              <div className="form-group math-question">
                <label htmlFor="mathAnswer">
                  Security Check: What is {mathQuestion.num1} + {mathQuestion.num2}? *
                </label>
                <input
                  type="text"
                  id="mathAnswer"
                  name="mathAnswer"
                  value={formData.mathAnswer}
                  onChange={handleChange}
                  className={errors.mathAnswer ? "error" : ""}
                  placeholder="Enter your answer"
                  data-testid="input-math"
                />
                {errors.mathAnswer && <span className="error-text">{errors.mathAnswer}</span>}
              </div>
              
              {errors.submit && (
                <div className="submit-error" data-testid="submit-error">
                  {errors.submit}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="btn-primary-zeta submit-btn"
                disabled={isSubmitting}
                data-testid="submit-consultation"
              >
                {isSubmitting ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ConsultationModal;
